/**
 * Un XAddr est une addresse d'un gap (interstice) ou d'un node dans une structure xml.
 * Un XAddr est constitué d'une pile d'items, chaque item correspond soit :
 * - à l'offset {number} du noeud fils lorsque le noeud courant est un document, fragment ou élément,
 * - au nom d'un attribut {string} lorsque le noeud courant est un élément,
 * - à l'offset {number} dans la chaine de caractère lorsque le noeud courant est un noeud texte, comment, PI ou attribut.
 *
 * Note d'impl : XAddr n'est qu'un tableau Js standard, pas une "class XAddr extends Array" pour éviter une duplication du tableau au parsing json.
 *
 */

import {IJmlObj, IJmlSet, JML} from "/@lib@/commons/xml/jml.js";

/**
 * Array json respectant le schéma XAddr et représentant l'adresse d'un noeud ou d'un interstice entre 2 noeuds dans un document xml.
 */
export interface IXAddr extends Array<number | string> {
}

/**
 * Range entre deux points de l'arbre. IXAddrRange.start DOIT précédé ou être égal à IXAddrRange.end.
 * Si end est null, le range est collapsé (ie caret vs selection).
 */
export interface IXAddrRange {
	start: IXAddr;
	end?: IXAddr;
}

export interface IXAddrRangeSel extends IXAddrRange {
	startInText?: boolean
	endInText?: boolean
	virtualPath?: Array<IJmlObj | '#' | '@'>
}

export interface IXAddrSeq {
	/**
	 * Point de départ de la séquence.
	 * Par convention, si XA.last(this.start) == -1, le set des attributs de l'élément container est inclus à la séquence.
	 */
	start: IXAddr;

	/**
	 * Longueur de la séquence.
	 * 0 revient à pointer un interstice entre deux noeuds.
	 * 1 revient à pointer le noeud à l'addresse IXAddrSeq.start.
	 * >1 revient à pointer une série continue de noeuds à partir de  IXAddrSeq.start.
	 * Si IXAddrSeq.start pointe un attribut, ie XA.isAttribute(IXAddrSeq.start), IXAddrSeq.len ne peut être que 0 ou 1.
	 */
	len: number;
}

export namespace XA {

	export function newAddr(): IXAddr {
		return [];
	}

	/**
	 * @param from XAddr ou Node à partir duquel construire ce nouvel xAddr.
	 */
	export function newBd(from?: IXAddr | Node | XAddrBuilder): XAddrBuilder {
		if (from == null) return new XAddrBuilder();
		if (Array.isArray(from)) return new XAddrBuilder(from.concat());
		if (from instanceof Node) return new XAddrBuilder(fromNode(from));
		if (from instanceof XAddrBuilder) return new XAddrBuilder(from.xa.concat());
		throw "XA.newBd(from) : from unknown : " + from;
	}

	/**
	 * @return Adresse non frozen.
	 */
	export function from(from: Node): IXAddr {
		return fromNode(from)
	}

	/**
	 * TODO merge with from()
	 * @return Adresse non frozen.
	 */
	export function fromNode(node: Node, root?: Node): IXAddr {
		const xa = [] as IXAddr;

		function pushXa(node: Node) {
			if (!node || !node.parentNode) return;
			pushXa(node.parentNode);
			let offs = 0;
			while (node.previousSibling) {
				offs++;
				node = node.previousSibling;
			}
			xa.push(offs);
			if (root && root === node.parentNode) return;
		}

		if (node instanceof Attr) {
			pushXa(node.ownerElement);
			xa.push(node.nodeName);
		} else pushXa(node);
		return xa;
	}

	/**
	 * Duplique un sous xa de 0 à depth.
	 * @param depth profondeur de la copie. si -1 élimine le noeud terminal, -2, le container...,
	 */
	export function subXa(xa: IXAddr, depth: number): IXAddr {
		const end = depth >= 0 ? depth : xa.length + depth;
		return xa.slice(0, end);
	}

	export function last(xa: IXAddr): number | string | null { return xa[xa.length - 1] }

	export function isAttribute(xa: IXAddr): boolean {
		return typeof xa[xa.length - 1] === 'string';
	}

	/** retourne un fragment d'un XAddr toujours sous forme de numérique : un attribut possède l'offset -1.*/
	export function offset(xa: IXAddr, depth: number): number {
		if (depth < 0) depth = xa.length + depth;
		const offs = xa[depth];
		return typeof offs === 'string' ? -1 : offs;
	}

	export function append(xa: IXAddr, ...items: Array<number | string>): IXAddr {
		//if(xa.find(entry=>typeof entry==='string') && items.find(entry=>typeof entry==='string')) debugger;
		if (Object.isFrozen(xa)) return xa.concat(items);
		Array.prototype.push.apply(xa, items);
		return xa;
	}

	export function up(xa: IXAddr): IXAddr {
		if (Object.isFrozen(xa)) return xa.slice(0, xa.length - 1);
		xa.length = xa.length - 1;
		return xa;
	}

	/**
	 * @param depth Si négatif part de la fin : -1 remaplce le fragment terminal, -2 son container, etc.
	 */
	export function setAtDepth(xa: IXAddr, depth: number, newVal: string | number): IXAddr {
		if (Object.isFrozen(xa)) xa = xa.concat();
		if (depth >= 0) {
			xa[depth] = newVal;
		} else {
			xa[xa.length + depth] = newVal;
		}
		return xa;
	}

	export function incrAtDepth(xa: IXAddr, depth: number, delta: number): IXAddr {
		if (Object.isFrozen(xa)) xa = xa.concat();
		if (depth < 0) depth = xa.length + depth;
		const val = xa[depth];
		if (typeof val === 'number') {
			xa[depth] = val + delta;
		} else throw Error(`Increment xAddr ${xa} at depth ${depth} failed.`);
		return xa;
	}

	export const freeze = Object.freeze as (xa: IXAddr) => IXAddr;

	export function newRangeAround(xaNode: IXAddr): IXAddrRange {
		if (xaNode.length === 0) return {start: XA.freeze([0]), end: XA.freeze([1])}; //document complet.
		const xaStart = XA.freeze(xaNode);
		return {start: xaStart, end: XA.isAttribute(xaStart) ? xaStart : XA.incrAtDepth(xaStart, -1, 1)};
	}

	export function cloneRange(range: IXAddrRange): IXAddrRange {
		return range != null ? {start: range.start, end: range.end} : null;
	}

	/**
	 * Retourne le node ou l'attribut correspondant à l'avant dernier item de cet xAddr.
	 * Représente le container d'une insertion ou supression, le dernier élemnt constituant l'offset
	 * de l'insertion ou de la supression.
	 *
	 * @return null si l'avant dernier élement n'existe pas ou n'est pas un node ou un attribut.
	 */
	export function findDomContainer(xa: IXAddr, node: Node): Node | Attr {
		try {
			return findDom(xa, node, xa.length - 1);
		} catch (e) {
			return null;
		}
	}

	/**
	 * Retourne le node ou l'attribut correspondant au dernier item de cet xAddr.
	 * Utlisé pour pointer un attribut à supprimer par exemple.
	 *
	 * @return null si le dernier élement n'existe pas ou n'est pas un node ou un attribut.
	 */
	export function findDomLast(xa: IXAddr, node: Node): Node | Attr | null {
		try {
			return findDom(xa, node, xa.length);
		} catch (e) {
			return null;
		}
	}

	/**
	 * Résoud ce xAddr jusqu'à la profondeur depth (exclue).
	 *
	 * @throws Will throw an error if target does not exist or is not a {Node|Attr}.
	 */
	export function findDom(xa: IXAddr, node: Node, beforeDepth: number): Node | Attr {
		for (let i = 0; i < beforeDepth; i++) {
			const item = xa[i];
			if (typeof item === 'number') {
				node = node.childNodes[item];
			} else {
				node = (node as Element).attributes.getNamedItem(item);
			}
		}
		return node;
	}

	export function cloneFragment(this: void, from: Node, to: Node | IJmlSet, range: IXAddrRange): Node | IJmlSet {
		let forkDepth = XA.findForkDepth(range.start, range.end);
		if (forkDepth === range.start.length) {
			if (forkDepth === range.end.length) return to; //les path égaux => range vide.
			//end est un sous-path de start, on remonte pour faire un exprt partiel du noeud start.
			forkDepth--;
		}
		let fromRoot = range.start[forkDepth];
		const toRoot = range.end[forkDepth];
		if (typeof fromRoot === 'string') throw Error(`Clone fragment throw attributes not allowed: ${range.start}`);
		if (typeof toRoot === 'string') throw Error(`Clone fragment throw attributes not allowed: ${range.end}`);
		const commonNode = XA.findDom(range.start, from, forkDepth);
		if (!commonNode) return to;
		if (commonNode instanceof CharacterData || commonNode instanceof Attr) {
			JML.appendText(commonNode.nodeValue.substring(fromRoot, toRoot), to);
			return to;
		}
		const childDepth = forkDepth + 1;

		let ch = commonNode.childNodes.item(fromRoot) as Node;
		if (ch && childDepth < range.start.length) {
			//on clone des containers partiels au début
			cloneAfter(ch, to, range.start, childDepth);
			ch = ch.nextSibling;
			fromRoot++;
		}

		//clone des noeuds complets (avant le dernier)
		while (ch && fromRoot++ < toRoot) {
			JML.appendDomNode(ch, true, to);
			ch = ch.nextSibling;
		}

		if (ch && range.end.length > childDepth) {
			//clone des noeuds partiels de fin
			cloneBefore(ch, to, range.end, childDepth);
		}
		return to;
	}

	export function cloneAfter(from: Node, to: Node | IJmlSet, xa: IXAddr, depth: number) {
		const start = xa[depth] || 0;
		if (typeof start === 'string') throw Error(`Clone fragment throw attributes not allowed: ${xa}`);
		if (from instanceof CharacterData) {
			let str = from.nodeValue;
			if (start < str.length) {
				if (start > 0) str = str.substring(start);
				JML.appendText(str, to);
			}
		} else {
			const subTo = JML.appendDomNode(from, false, to);
			let ch = from.childNodes.item(start) as Node;
			if (ch) {
				if (depth === xa.length - 1) {
					//noeud fils final de xa
					JML.appendDomNode(ch, true, subTo);
				} else {
					cloneAfter(ch, subTo, xa, depth + 1);
				}
				ch = ch.nextSibling;
			}
			while (ch) {
				//on clone les noeuds suivants
				JML.appendDomNode(ch, true, subTo);
				ch = ch.nextSibling;
			}
		}
	}

	export function cloneBefore(from: Node, to: Node | IJmlSet, xa: IXAddr, depth: number) {
		const end = xa[depth];
		if (typeof end === 'string') throw Error(`Clone fragment attributes not allowed: ${xa}`);
		if (from instanceof CharacterData) {
			let str = from.nodeValue;
			if (end === undefined || end > 0) {
				if (end > 0) str = str.substring(0, end);
				JML.appendText(str, to);
			}
		} else {
			const subTo = JML.appendDomNode(from, false, to);
			let ch = from.firstChild;
			let offset = 0;
			const last = depth === xa.length - 1 ? end - 1 : end === undefined ? Infinity : end;
			while (ch && offset++ < last) {
				//on clone les noeuds précédant l'offset pointé
				JML.appendDomNode(ch, true, subTo);
				ch = ch.nextSibling;
			}
			//on clone le noeud juste avant range.end
			if (ch) {
				if (depth === xa.length - 1) {
					//noeud fils final de xa
					if (end > 0) JML.appendDomNode(ch, true, subTo);
				} else {
					cloneBefore(ch, subTo, xa, depth + 1);
				}
			}
		}
	}

	/** Attention, ne peut être un xa qui pointe un offset dans un noeud text ou dans un attribut */
	export function buildXpath(xa: IXAddr): string {
		const buf: string[] = [];
		for (let i = xa.length - 1; i >= 0; i--) {
			const frag = xa[i];
			if (typeof frag === "number") {
				buf.push(`node()[${frag + 1}]`)
			} else if (typeof frag === "string") {
				buf.push(`@${frag}`)
			}
		}
		return buf.reverse().join("/");
	}

	export function isEquals(xa1: IXAddr, xa2: IXAddr): boolean {
		if (xa1.length !== xa2.length) return false;
		for (let i = 0; i < xa1.length; i++) if (xa1[i] !== xa2[i]) return false;
		return true;
	}

	export function isAncOrEquals(xaSup: IXAddr, xaSub: IXAddr): boolean {
		if (xaSup.length > xaSub.length) return false;
		for (let i = 0; i < xaSup.length; i++) if (xaSup[i] !== xaSub[i]) return false;
		return true;
	}

	export function isAnc(xaSup: IXAddr, xaSub: IXAddr): boolean {
		if (xaSup.length >= xaSub.length) return false;
		for (let i = 0; i < xaSup.length; i++) if (xaSup[i] !== xaSub[i]) return false;
		return true;
	}

	/** Pour eval a < b avec isBefore(a,b) ou a >= b avec !isBefore(b,a) */
	export function isBefore(xaBefore: IXAddr, xaAfter: IXAddr): boolean {
		const depth = Math.min(xaBefore.length, xaAfter.length);
		for (let i = 0; i < depth; i++) {
			//FIXME algo à revoir si xaBefore et xaAfter sont 2 attr d'un noeud
			const offsBef = XA.offset(xaBefore, i);
			const offsAft = XA.offset(xaAfter, i);
			if (offsBef < offsAft) return true;
			if (offsBef > offsAft) return false;
		}
		return xaBefore.length < xaAfter.length;
	}

	/** Pour eval a > b avec isAfter(a,b) ou a <= b avec !isAfter(b,a) */
	export function isAfter(xaAfter: IXAddr, xaBefore: IXAddr): boolean {
		const depth = Math.min(xaBefore.length, xaAfter.length);
		for (let i = 0; i < depth; i++) {
			//FIXME algo à revoir si xaBefore et xaAfter sont 2 attr d'un noeud
			const offsBef = XA.offset(xaBefore, i);
			const offsAft = XA.offset(xaAfter, i);
			if (offsBef < offsAft) return true;
			if (offsBef > offsAft) return false;
		}
		return xaAfter.length > xaBefore.length;
	}

	/**
	 * Les 2 xa pointent 2 offsets dans la même séquence.
	 */
	export function isInSameSeq(xa1: IXAddr, xa2: IXAddr): boolean {
		if (xa1.length !== xa2.length) return false;
		const lastIdx = xa1.length - 1;
		if (typeof xa1[lastIdx] === 'string' || typeof xa2[lastIdx] === 'string') return false;
		for (let i = 0; i < lastIdx; i++) if (xa1[i] !== xa2[i]) return false;
		return true;
	}

	/**
	 * xaSub est dans la séquence [xaStart, xaStart+len].
	 * Si xaStart est un attribut, len n'est pas exploité et évalue si xaSub est dans l'attribut.
	 */
	export function isInSeq(xaStart: IXAddr, len: number, xaSub: IXAddr, inlcudeStart = false, includeEnd = false): boolean {
		if (xaStart.length > xaSub.length) return false;
		const lastIdx = xaStart.length - 1;
		if (lastIdx < 0) return true;
		for (let i = 0, s = lastIdx; i < s; i++) if (xaStart[i] !== xaSub[i]) return false;
		const start = xaStart[lastIdx];
		const offset = xaSub[lastIdx];
		if (typeof start === 'string') return start === offset; //xaSub dans attr xaStart
		if (typeof offset === 'string') return false; //xaSub est un attr != de xaStart
		if (xaStart.length === xaSub.length) {
			return (inlcudeStart ? offset >= start : offset > start) && (includeEnd ? offset <= start + len : offset < start + len);
		} else {
			//xaSub est un sous noeud de xaStart
			return offset >= start && offset < start + len;
		}
	}

	/** Les 2 xa pointent 2 offsets dans la même séquence et la première est avant la seconde. */
	export function isBeforeInSameSeq(xaBefore: IXAddr, xaAfter: IXAddr, orEqual?: boolean): boolean {
		if (xaBefore.length !== xaAfter.length) return false;
		const lastIdx = xaBefore.length - 1;
		if (typeof xaBefore[lastIdx] === 'string' || typeof xaAfter[lastIdx] === 'string') return false;
		if (orEqual ? xaBefore[lastIdx] > xaAfter[lastIdx] : xaBefore[lastIdx] >= xaAfter[lastIdx]) return false;
		for (let i = 0; i < lastIdx; i++) if (xaBefore[i] !== xaAfter[i]) return false;
		return true;
	}

	export function isInRange(rg: IXAddrRange, xa: IXAddr, excludeStart?: boolean, excludeEnd?: boolean): boolean {
		if (excludeStart && XA.isEquals(rg.start, xa)) return false;
		if (XA.isBefore(xa, rg.start)) return false;
		if (excludeEnd && XA.isEquals(rg.end, xa)) return false;
		if (XA.isBefore(rg.end, xa)) return false;
		return true;
	}

	/** @param biasBefore si true, xaToMove ne sera pas déplacé si il est égal au point d'insertion. */
	export function translateInsSeq(xaToMove: IXAddr, xaInsert: IXAddr, len: number, biasBefore?: boolean): IXAddr {
		if (xaToMove.length < xaInsert.length) return xaToMove;
		const s = xaInsert.length - 1;
		for (let i = 0; i < s; i++) if (xaInsert[i] !== xaToMove[i]) return xaToMove;
		//xaToMove et xaPoint sont bien dans la même séquence
		const offsetToMove = xaToMove[s];
		if (typeof offsetToMove === 'string') return xaToMove; //xaToMove est un attr, hors séquence des fils
		const startIns = xaInsert[s];
		if (typeof startIns === 'string') return xaToMove; //xaInsert est un attr, hors séquence des fils
		if (biasBefore ? offsetToMove <= startIns : offsetToMove < startIns) return xaToMove; //xaToMove est avant le mouvement, pas de changement
		return setAtDepth(xaToMove, s, offsetToMove + len);
	}

	/** @param biasBefore si true, les bornes ne seront pas déplacées si elles sont égale au point d'insertion. */
	export function translateInsSeqRange(range: IXAddrRange, xaInsert: IXAddr, len: number, biasBefore?: boolean): IXAddrRange {
		if (Object.isFrozen(range)) range = cloneRange(range);
		if (range.start) range.start = translateInsSeq(range.start, xaInsert, len, biasBefore);
		if (range.end) range.end = translateInsSeq(range.end, xaInsert, len, biasBefore);
		return range;
	}

	export function translateDelSeq(xaToMove: IXAddr, xaDelStart: IXAddr, len: number, nullIfInDel?: boolean): IXAddr {
		if (xaToMove.length < xaDelStart.length) return xaToMove;
		const s = xaDelStart.length - 1;
		for (let i = 0; i < s; i++) if (xaDelStart[i] !== xaToMove[i]) return xaToMove;
		//xaToMove et xaPoint sont bien dans la même séquence
		const offsetToMove = xaToMove[s];
		const startDel = xaDelStart[s];
		if (typeof startDel === 'string') {
			//suppr d'un attr
			if (startDel === offsetToMove) return nullIfInDel ? null : xaDelStart; //xaToMove est écrasé par ce delete.
			return xaToMove; //pas d'interférence
		}
		if (typeof offsetToMove === 'string') return xaToMove; //xaToMove est un attr, hors séquence des fils
		if (offsetToMove < startDel) return xaToMove; //xaToMove est avant le mouvement, pas de changement
		if (offsetToMove < startDel + len) return nullIfInDel ? null : xaDelStart; //xaToMove est dans la del.
		return setAtDepth(xaToMove, s, offsetToMove - len);
	}

	export function translateDelSeqRange(range: IXAddrRange, xaDelStart: IXAddr, len: number, nullIfInDel?: boolean): IXAddrRange {
		if (Object.isFrozen(range)) range = cloneRange(range);
		if (range.start) range.start = translateDelSeq(range.start, xaDelStart, len, nullIfInDel);
		if (range.end) range.end = translateDelSeq(range.end, xaDelStart, len, nullIfInDel);
		return range;
	}

	export function isCollapsed(rg: IXAddrRange): boolean {
		if (!rg.end) return true;
		return isEquals(rg.start, rg.end);
	}

	/**
	 * Recherche la profondeur du point de divergence entre deux adresses.
	 * @return La profondeur divergente.
	 *  - [1,3] et [1,4] -> 1
	 *  - [1,3] et [1,3,1] -> 2
	 *  - [0,1] et [1,3,1] -> 0
	 *  - [1,3] et [1,3] -> 2
	 */
	export function findForkDepth(xa1: IXAddr, xa2: IXAddr): number {
		const maxDepth = Math.min(xa1.length, xa2.length);
		for (let d = 0; d < maxDepth; d++) if (xa1[d] !== xa2[d]) return d;
		return maxDepth;
	}

	export interface OXARange2SeqsOption {
		/**
		 * Pour les noeuds containers de la fin du range dont le contenu est partiellement supprimé,
		 * leurs attributs doivent-ils être inclus ou exclus ?
		 * L'inclusion des attributs est matérialisé par la valeur -1 de la dernière part de IXAddrSeq.start.
		 */
		excludeAtts?: boolean;
	}

	/** Produit toutes les séquences intégralement incluses dans le range. */
	export function range2Seqs(range: IXAddrRange, options?: OXARange2SeqsOption): IXAddrSeq[] {
		let commonDepth = XA.findForkDepth(range.start, range.end);
		let startDepth = range.start.length;

		let fromRoot = range.start[commonDepth];
		const toRoot = range.end[commonDepth];
		if (typeof fromRoot === 'string') throw Error(`Leaf bound range can not be an attribute : ${range.start}`);
		if (typeof toRoot === 'string') throw Error(`Leaf bound range can not be an attribute : ${range.end}`);

		const res = [] as IXAddrSeq[];
		if (startDepth - 1 > commonDepth) {
			res.push({start: XA.subXa(range.start, startDepth--), len: Infinity});
			while (--startDepth > commonDepth) {
				res.push({start: XA.append(XA.subXa(range.start, startDepth), XA.offset(range.start, startDepth) + 1), len: Infinity});
			}
			fromRoot++;
		}

		const endDepth = range.end.length;
		if (endDepth > commonDepth) {
			if (toRoot > fromRoot) res.push({start: XA.append(XA.subXa(range.start, commonDepth), fromRoot), len: toRoot - fromRoot});
			else if (fromRoot === undefined) commonDepth--;
			if (options && options.excludeAtts) {
				for (let i = commonDepth + 1; i < endDepth; i++) {
					const len = XA.offset(range.end, i);
					if (len > 0) res.push({start: XA.append(XA.subXa(range.end, i), 0), len: len});
				}
			} else {
				for (let i = commonDepth + 1; i < endDepth; i++) {
					res.push({start: XA.append(XA.subXa(range.end, i), -1), len: XA.offset(range.end, i) + 1});
				}
			}
		} else if (fromRoot !== undefined /* fromRoot undefined si le range est collapsed.*/) {
			res.push({start: XA.subXa(range.start, commonDepth + 1), len: toRoot - fromRoot});
		}
		return res;
	}

	export function range2StartSeq(rg: IXAddrRange): IXAddrSeq {
		if (isCollapsed(rg)) return {start: rg.start, len: 0};
		const commonDepth = XA.findForkDepth(rg.start, rg.end);
		if (commonDepth === rg.start.length) return {start: rg.start, len: 0};
		if (commonDepth === rg.start.length - 1) return {start: rg.start, len: rg.end[commonDepth] as number - (rg.start[commonDepth] as number)};
		return {start: rg.start, len: Infinity};
	}

}

export class XAddrBuilder {
	constructor(public xa: IXAddr = []) {
	}

	append(...items: Array<number | string>): XAddrBuilder {
		//if(this.xa.find(entry=>typeof entry==='string') && items.find(entry=>typeof entry==='string')) debugger;
		this.xa.push(...items);
		return this;
	}

	up(): XAddrBuilder {
		this.xa.length = this.xa.length - 1;
		return this;
	}

	/**
	 *
	 * @param depth Si négatif part de la fin : -1 remaplce le fragment terminal, -2 son container, etc.
	 */
	setAtDepth(depth: number, newVal: number | string): XAddrBuilder {
		if (depth >= 0) {
			this.xa[depth] = newVal;
		} else {
			this.xa[this.xa.length + depth] = newVal;
		}
		return this;
	}


	/**
	 *
	 * @param depth Si négatif part de la fin : -1 remaplce le fragment terminal, -2 son container, etc.
	 * @param décalage à appliquer.
	 * @return {XAddrBuilder}
	 */
	incrAtDepth(depth: number, delta: number): XAddrBuilder {
		if (depth < 0) depth = this.xa.length + depth;
		const val = this.xa[depth];
		if (typeof val === 'number') {
			this.xa[depth] = val + delta;
		} else throw Error(`Increment xAddr ${this.xa} at depth ${depth} failed.`);
		return this;
	}

	freeze(): IXAddr { return XA.freeze(this.xa) }
}
